import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseKeyValue } from "@/shared/ui/BaseKeyValue";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import {
  Backdrop,
  Box,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { AccountRequest, AccountRequestListPageProps } from "../model";

const formSchema = z.object({
  password: z.string({ required_error: "パスワードを入力してください" }).min(8),
});

export type AccountRequestDetailDialogContentProps = {
  accountRequest: AccountRequest;
  status: AccountRequestListPageProps["selectedAccountRequestApprovalStatus"];
  onCloseClick: () => void;
  onApproveClick: (password: string) => void;
  onRejectClick: () => void;
};
export const AccountRequestDetailDialogContent: React.FC<
  AccountRequestDetailDialogContentProps
> = (props) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState<string>();
  const [passwordError, setPasswordError] = useState<z.ZodIssue>();

  const bodyApproved = (
    <Stack spacing={4} px={8} pb={8}>
      <Stack direction="row" alignItems="end">
        <Typography variant="body1" fontWeight={700}>
          アカウント登録申請を承認しました。ログイン情報を共有してください。
        </Typography>
        <Box flex={1} />
        <BaseButton onClick={props.onCloseClick}>
          <BaseIcon name="xClose" color="action" width={24} height={24} />
        </BaseButton>
      </Stack>
      <Stack spacing={1}>
        <Typography variant="body2">
          会社名：{props.accountRequest.companyName}
        </Typography>
        <Typography variant="body2">
          担当者：{props.accountRequest.lastName}{" "}
          {props.accountRequest.firstName}
        </Typography>
        <Typography variant="body2">
          メールアドレス：{props.accountRequest.email}
        </Typography>
        <Typography variant="body2">パスワード：{password}</Typography>
      </Stack>
    </Stack>
  );

  const bodyRejected = (
    <Stack spacing={4} px={8} pb={8}>
      <Stack direction="row" alignItems="end">
        <Typography variant="body1" fontWeight={700}>
          アカウント登録申請を却下しました。
        </Typography>
        <Box flex={1} />
        <BaseButton onClick={props.onCloseClick}>
          <BaseIcon name="xClose" color="action" width={24} height={24} />
        </BaseButton>
      </Stack>
      <Stack spacing={1}>
        <Typography variant="body2">
          会社名：{props.accountRequest.companyName}
        </Typography>
        <Typography variant="body2">
          担当者：{props.accountRequest.lastName}{" "}
          {props.accountRequest.firstName}
        </Typography>
        <Typography variant="body2">
          メールアドレス：{props.accountRequest.email}
        </Typography>
      </Stack>
    </Stack>
  );

  return (
    <Stack p={4} spacing={3}>
      <Backdrop
        open={props.status === "loading"}
        sx={{ zIndex: 999, color: "white" }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {props.status === "approved" ? (
        bodyApproved
      ) : props.status === "rejected" ? (
        bodyRejected
      ) : (
        <Stack spacing={2}>
          <Stack direction="row" alignItems="end">
            <Typography variant="h2" fontSize="18px" fontWeight={700}>
              アカウント登録申請を承認してください
            </Typography>
            <Box flex={1} />
            <BaseButton onClick={props.onCloseClick}>
              <BaseIcon name="xClose" color="action" width={24} height={24} />
            </BaseButton>
          </Stack>
          <Box>
            <BaseKeyValue keyLabel="会社情報">
              <Box>
                種別：
                <Stack direction="row" spacing={1}>
                  {props.accountRequest.companyCategory?.map((c) => t(c))}
                </Stack>
              </Box>
              <Box>社名：{props.accountRequest.companyName}</Box>
              <Box>事業所名：{props.accountRequest.officeName}</Box>
            </BaseKeyValue>
            <BaseKeyValue keyLabel="担当者">
              <Box>
                氏名：{props.accountRequest.lastName}{" "}
                {props.accountRequest.firstName}
              </Box>
              <Box>
                カナ：{props.accountRequest.lastNameKana}{" "}
                {props.accountRequest.firstNameKana}
              </Box>
              <Box>メールアドレス：{props.accountRequest.email}</Box>
              <Box>電話番号：{props.accountRequest.phoneNumber}</Box>
            </BaseKeyValue>
          </Box>
          <Stack spacing={2}>
            <BaseFormLabel label="パスワード" sx={{ mt: 3 }}>
              <Box display="flex" gap={1} sx={{ alignItems: "flex-end" }}>
                <TextField
                  size="small"
                  error={!!passwordError}
                  helperText={passwordError?.message}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                    },
                  }}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                />
              </Box>
            </BaseFormLabel>
            <BaseButton
              variant="contained"
              onClick={() => {
                const parmas = formSchema.safeParse({ password });
                if (!parmas.success) {
                  const passwordError = parmas.error?.errors?.find(
                    (error) => error.path[0] === "password",
                  );
                  setPasswordError(passwordError);
                  return;
                }
                setPasswordError(undefined);
                props.onApproveClick(parmas.data.password);
              }}
            >
              承認
            </BaseButton>
            <BaseButton
              variant="contained"
              color="error"
              onClick={props.onRejectClick}
            >
              却下
            </BaseButton>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
